import { useCallback, useEffect, useRef, useState } from 'react';

// "Not now" holds for the rest of the app session (until a reload or the app
// is closed), on every page, as the MIDI blocker's close does.
let declinedThisSession = false;

/** Forget a "Not now" (a new app session; also for specs). */
export const forgetKeyboardPromptAnswer = (): void => {
  declinedThisSession = false;
};

export interface KeyboardConnectedPromptState {
  open: boolean;
  /** The player chose to use the keyboard and it was saved. */
  close: () => void;
  /** "Not now": keep the answer and do not ask again this session. */
  decline: () => void;
}

/**
 * Whether to ask "Keyboard connected. Use it so MuseFlow can hear you?".
 *
 * Asked when a MIDI keyboard connects while the account says the player plays
 * on something the app cannot hear. A keyboard already connected when the
 * player changes their answer is not a new connection, so it does not ask.
 * A connection seen before the account has loaded (or on a page that does not
 * ask, such as the welcome flow) waits until it can be asked. Unplugging the
 * keyboard drops the question. Nothing changes without the player's answer.
 */
export function useKeyboardConnectedPrompt({
  deviceConnected,
  graded,
  canAsk,
}: {
  /** True while a MIDI keyboard is connected. */
  deviceConnected: boolean;
  /** True when the account's answer is an input the app can hear. */
  graded: boolean;
  /** True where the question can be asked: the account is loaded and stores the answer. */
  canAsk: boolean;
}): KeyboardConnectedPromptState {
  const [connectionToAskAbout, setConnectionToAskAbout] = useState(false);
  const [open, setOpen] = useState(false);
  const wasConnectedRef = useRef(false);

  useEffect(() => {
    if (deviceConnected && !wasConnectedRef.current) setConnectionToAskAbout(true);
    if (!deviceConnected) {
      setConnectionToAskAbout(false);
      setOpen(false);
    }
    wasConnectedRef.current = deviceConnected;
  }, [deviceConnected]);

  useEffect(() => {
    if (!connectionToAskAbout || !canAsk) return;
    setConnectionToAskAbout(false);
    if (!graded && !declinedThisSession) setOpen(true);
  }, [connectionToAskAbout, canAsk, graded]);

  // Switched to the keyboard some other way: the question is answered.
  useEffect(() => {
    if (graded) setOpen(false);
  }, [graded]);

  const close = useCallback(() => setOpen(false), []);
  const decline = useCallback(() => {
    declinedThisSession = true;
    setOpen(false);
  }, []);

  return { open, close, decline };
}
